import type { Iso2 } from 'intl-tel-input/data'

export const supportedLanguages = ['en', 'fr', 'nl'] as const
export type SupportedLanguage = (typeof supportedLanguages)[number]

export const countriesWithPublicHolidays = ['FR', 'BE', 'NL'] as const
export type CountryWithPublicHolidays = (typeof countriesWithPublicHolidays)[number]

type Locale = `${SupportedLanguage}-${Uppercase<string>}`

type PhoneConfig = {
  countryCode: Iso2 | Uppercase<Iso2>
}

type DateFormats = {
  compact: string // Short date format (e.g., 01/01/2024 or 1/1/2024)
  compactTime: string // Short date with time (e.g., 01/01/2024 14:05)
}

type AddressLine1Key = 'streetNumber' | 'streetType' | 'streetName'
type AddressLine1Format = AddressLine1Key[]

type SiteLocalizationConfig = {
  locale: Locale
  timeZone?: string
  phone: PhoneConfig
  dateFormats: DateFormats
  addressLine1Format: AddressLine1Format
}

type SiteLocalization = {
  language: SupportedLanguage
} & SiteLocalizationConfig

export const fallbackLanguage: SupportedLanguage = 'en'

const localizationByLanguage = {
  en: {
    locale: 'en-US',
    timeZone: 'UTC',
    phone: { countryCode: 'US' },
    dateFormats: { compact: 'MM/DD/YYYY', compactTime: 'MM/DD/YYYY HH:mm' },
    addressLine1Format: ['streetNumber', 'streetType', 'streetName'],
  },
  fr: {
    locale: 'fr-FR',
    timeZone: 'Europe/Paris',
    phone: { countryCode: 'FR' },
    dateFormats: { compact: 'DD/MM/YYYY', compactTime: 'DD/MM/YYYY HH:mm' },
    addressLine1Format: ['streetNumber', 'streetName'],
  },
  nl: {
    locale: 'nl-NL',
    timeZone: 'Europe/Amsterdam',
    phone: { countryCode: 'NL' },
    dateFormats: { compact: 'DD-MM-YYYY', compactTime: 'DD-MM-YYYY HH:mm' },
    addressLine1Format: ['streetType', 'streetName', 'streetNumber'],
  },
} satisfies Record<SupportedLanguage, SiteLocalizationConfig>

const brandLanguageMapping = {
  prolians: 'fr',
  hydralians: 'fr',
  dexis: 'fr',
  destil_nl: 'nl',
  ecom_en: 'en',
  dexis_nl: 'nl',
} satisfies Record<Brands, SupportedLanguage>

/**
 * buildLocaleConfig(locale, timeZone, countryCode, dateFormats?): Partial<SiteLocalizationConfig>
 * Helper to derive locale info (tz, phone, dateFormats) for a language/country tuple; returns a partial config.
 */
const buildLocaleConfig = (
  locale: Locale,
  timeZone: string,
  countryCode: Iso2 | Uppercase<Iso2>,
  dateFormats?: DateFormats,
  addressLine1Format?: AddressLine1Format,
): Partial<SiteLocalizationConfig> => ({
  locale,
  timeZone,
  phone: { countryCode },
  ...(dateFormats && { dateFormats }),
  ...(addressLine1Format && { addressLine1Format }),
})

/**
 * buildLocalizationOverrides(locales, countryCode, timeZone, dateFormats?): per-language overrides for one brand.
 * Maps a brand's languages to full locale info (tz/phone/dateFormats included), leaving gaps untouched; returns a partial record keyed by language.
 */
const buildLocalizationOverrides = (
  locales: Partial<Record<SupportedLanguage, Locale>>,
  countryCode: Iso2 | Uppercase<Iso2>,
  timeZone: string,
  dateFormats?: DateFormats,
  addressLine1Format?: AddressLine1Format,
): { [L in SupportedLanguage]?: Partial<SiteLocalizationConfig> } =>
  Object.entries(locales).reduce(
    (acc, [language, locale]) => {
      if (!locale) return acc
      acc[language as SupportedLanguage] = buildLocaleConfig(
        locale,
        timeZone,
        countryCode,
        dateFormats,
        addressLine1Format,
      )

      return acc
    },
    {} as { [L in SupportedLanguage]?: Partial<SiteLocalizationConfig> },
  )

type BrandLocalizationOverrides = Record<
  Brands,
  Partial<Record<SupportedLanguage, Partial<SiteLocalizationConfig>>>
>

const brandLocalizationOverrides: BrandLocalizationOverrides = {
  prolians: {},
  hydralians: {},
  dexis: {},
  destil_nl: buildLocalizationOverrides({ en: 'en-NL' }, 'NL', 'Europe/Amsterdam', {
    compact: 'DD-MM-YYYY',
    compactTime: 'DD-MM-YYYY HH:mm',
  }),
  ecom_en: {},
  dexis_nl: buildLocalizationOverrides({ nl: 'nl-BE', en: 'en-BE' }, 'NL', 'Europe/Amsterdam', {
    compact: 'DD/MM/YYYY',
    compactTime: 'DD/MM/YYYY HH:mm',
  }),
}

const normalizeLanguage = (language?: string | null): SupportedLanguage | undefined => {
  if (!language) return undefined

  const lowerCased = language.toLowerCase()

  return supportedLanguages.find((lang) => lowerCased.startsWith(lang))
}

/**
 * getSiteLocalization(languageOverride?, brandOverride?): resolves the active localization bundle
 * (language, locale, timeZone, phone, dateFormats) using overrides, env defaults, and brand mapping; returns the full bundle.
 */
export function getSiteLocalization(
  languageOverride?: string | null,
  brandOverride?: Brands,
): SiteLocalization {
  const language =
    normalizeLanguage(languageOverride) ??
    (brandOverride && brandLanguageMapping[brandOverride]) ??
    normalizeLanguage(process.env.NEXT_PUBLIC_I18N_DEFAULT_LOCALE) ??
    (process.env.NEXT_PUBLIC_BRAND &&
      brandLanguageMapping[process.env.NEXT_PUBLIC_BRAND as Brands]) ??
    fallbackLanguage

  const brand = brandOverride ?? (process.env.NEXT_PUBLIC_BRAND as Brands | undefined)

  const localization = {
    ...localizationByLanguage[language],
    ...(brand ? brandLocalizationOverrides[brand]?.[language] : undefined),
  }

  return { language, ...localization }
}
